import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../../services/adminApiService';
import { Mail, Search, RefreshCw, MessageSquare, Phone, User, Calendar } from 'lucide-react';

export const AdminMessagesView: React.FC = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedMessage, setSelectedMessage] = useState<any | null>(null);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const data = await adminApiService.getMessages();
      if (data.success) {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const filtered = messages.filter(
    (m) =>
      m.sender?.toLowerCase().includes(search.toLowerCase()) ||
      m.email?.toLowerCase().includes(search.toLowerCase()) ||
      m.website?.toLowerCase().includes(search.toLowerCase()) ||
      m.subject?.toLowerCase().includes(search.toLowerCase()) ||
      m.content?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-600" />
            <span>Müşteri İletişim Formu Mesajları</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Yayınlanan müşteri web sitelerinin iletişim formları üzerinden gelen gerçek ziyaretçi mesajlarını inceleyin.
          </p>
        </div>
        <button
          onClick={loadMessages}
          disabled={loading}
          className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-2 self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Gönderen, e-posta, web sitesi veya mesaj içeriği ile ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/20 bg-white"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">Web Sitesi</th>
                  <th className="py-3 px-4">Gönderen</th>
                  <th className="py-3 px-4">Konu</th>
                  <th className="py-3 px-4">Tarih</th>
                  <th className="py-3 px-4 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-purple-500" />
                      Mesajlar yükleniyor...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400">
                      Henüz mesaj bulunmuyor.
                    </td>
                  </tr>
                ) : (
                  filtered.map((m) => (
                    <tr
                      key={m.id}
                      onClick={() => setSelectedMessage(m)}
                      className={`cursor-pointer transition ${
                        selectedMessage?.id === m.id ? 'bg-purple-50/70' : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="py-3.5 px-4 font-semibold text-slate-900">
                        {m.website}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="text-slate-800 font-medium">{m.sender}</div>
                        <div className="text-[11px] text-slate-400">{m.email}</div>
                      </td>
                      <td className="py-3.5 px-4 text-slate-700 max-w-[160px] truncate">
                        {m.subject || m.content}
                      </td>
                      <td className="py-3.5 px-4 text-slate-500 font-mono text-[11px]">
                        {m.date}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMessage(m);
                          }}
                          className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-purple-100 text-purple-700 text-xs font-semibold"
                        >
                          Oku
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Message Details Preview */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs">
          {selectedMessage ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Mesaj Detayı</span>
                <span className="text-[11px] text-slate-400 font-mono">{selectedMessage.date}</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <User className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                  <span className="font-semibold text-slate-900">{selectedMessage.sender}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Mail className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                  <a href={`mailto:${selectedMessage.email}`} className="text-purple-600 hover:underline">
                    {selectedMessage.email}
                  </a>
                </div>
                {selectedMessage.phone && (
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Phone className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                    <span>{selectedMessage.phone}</span>
                  </div>
                )}
                <div className="text-xs text-slate-500 pt-1">
                  Kaynak Site: <span className="font-semibold text-slate-800">{selectedMessage.website}</span>
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs font-bold text-slate-800 mb-1">{selectedMessage.subject}</div>
                <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {selectedMessage.content}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-16 text-center text-slate-400">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-xs">Detaylarını görmek için soldan bir mesaj seçin.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
